import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import { useEffect, useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';

import {
  useDeleteDeviceSirenLinkMutation,
  useGetConnectedDevicesQuery,
  useGetDeviceSirenLinkQuery,
  useSetDeviceSirenLinkMutation,
} from 'services/devices/devices.api';

export const SIREN_TYPE = 'Technology Bridge_Siren/Giant Voice';
export const CAMERA_TYPE = 'Overwatch VMS';

/**
 * DevicePairing — pair a VMS camera with a siren, from either side.
 *
 * THE LINK IS ONE ROW, ADDRESSED BY THE CAMERA
 *
 * `RI_Camera_Siren_Map` holds one camera ↔ one siren. The backend exposes it
 * on the camera: `PUT/DELETE /device/:cameraId/siren`. A pairing made from the
 * siren's form therefore still calls those routes, with the chosen camera as
 * `:cameraId` and this siren as `siren_uid`. Pairing a camera that already
 * belongs to another siren re-points it — the backend clears the old row on
 * both sides — so such cameras are labelled in the picker.
 *
 * WHAT THE ROW UNLOCKS
 *
 * `device.camera` on a siren is what reveals its VMS controls, its "Manage
 * Audio Files" button and the Audio Manager (upload and record). Until this
 * component existed there was no way to make the row from the UI at all.
 *
 * TWO MODES, ONE COMPONENT
 *
 *   mode="camera"  the form is a VMS camera; pick the siren it serves
 *   mode="siren"   the form is a siren; pick the camera that serves it
 *
 * `DeviceModal` serves add AND edit, so both entry points — adding the second
 * device of a pair, or editing either device later from Connected Devices —
 * come through here.
 *
 * ADD vs EDIT
 *
 * On EDIT the device has an id, so the choice is saved immediately. On ADD
 * there is no id yet — the choice is held in the form (`siren_uid` on a
 * camera, `camera_uid` on a siren) and the parent applies it once the device
 * exists. Hidden behind "Advanced", closed by default: most devices are not
 * paired.
 */
export default function DevicePairing({ mode, formData, setFormData, device }) {
  const isEdit = Boolean(device?.id);
  const pickingSiren = mode === 'camera';
  const formKey = pickingSiren ? 'siren_uid' : 'camera_uid';

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();

  // Camera form: the stored link is read from the camera's own resource.
  // Siren form: the list already carries the paired camera as `device.camera`.
  const { data: cameraLink } = useGetDeviceSirenLinkQuery(device?.id, {
    skip: !(isEdit && pickingSiren),
  });
  const currentValue = pickingSiren
    ? (cameraLink?.siren_uid ?? null)
    : (connectedDevices.find((d) => d?.id === device?.id)?.camera ?? device?.camera ?? null);

  const [setLink] = useSetDeviceSirenLinkMutation();
  const [removeLink] = useDeleteDeviceSirenLinkMutation();

  const wantedType = pickingSiren ? SIREN_TYPE : CAMERA_TYPE;
  const candidates = useMemo(
    () => connectedDevices.filter((d) => d?.device_type === wantedType),
    [connectedDevices, wantedType],
  );

  // Which siren each camera currently serves, from the sirens' `camera` field,
  // so the picker can say when choosing a camera would take it from another siren.
  const cameraOwner = useMemo(() => {
    const owners = new Map();
    for (const d of connectedDevices) {
      if (d?.device_type === SIREN_TYPE && d?.camera) owners.set(Number(d.camera), d);
    }
    return owners;
  }, [connectedDevices]);

  const [selected, setSelected] = useState('');
  useEffect(() => {
    if (isEdit) setSelected(currentValue ? String(currentValue) : '');
    else setSelected(formData?.[formKey] ? String(formData[formKey]) : '');
  }, [isEdit, currentValue, formData?.[formKey], formKey]);

  // Open the section on its own when a pairing already exists.
  useEffect(() => {
    if (currentValue) setOpen(true);
  }, [currentValue]);

  const onChange = async (value) => {
    setSelected(value);
    setError(null);

    if (!isEdit) {
      setFormData((prev) => ({ ...prev, [formKey]: value ? Number(value) : undefined }));
      return;
    }

    setBusy(true);
    try {
      if (pickingSiren) {
        if (value) await setLink({ cameraId: device.id, sirenUid: Number(value) }).unwrap();
        else await removeLink(device.id).unwrap();
      } else if (value) {
        await setLink({ cameraId: Number(value), sirenUid: device.id }).unwrap();
      } else if (currentValue) {
        await removeLink(Number(currentValue)).unwrap();
      }
    } catch (err) {
      // Never show a pairing that was not saved.
      setSelected(currentValue ? String(currentValue) : '');
      setError(err?.data?.message || 'Could not save the pairing.');
    } finally {
      setBusy(false);
    }
  };

  const label = pickingSiren ? 'Paired siren (optional)' : 'Paired camera (optional)';
  const emptyHint = pickingSiren
    ? 'No sirens in this workplace yet. Add the siren first, then pair it here.'
    : 'No VMS cameras in this workplace yet. Add the camera first, then pair it here.';
  const hint = pickingSiren
    ? 'Pairing lets this camera serve the siren’s Listen Live and VMS controls.'
    : 'Pairing gives this siren its Listen Live, VMS and audio controls.';

  const optionLabel = (d) => {
    const name = d.alias || d.name;
    if (pickingSiren) return name;
    const owner = cameraOwner.get(Number(d.id));
    if (owner && owner.id !== device?.id) return `${name} — paired to ${owner.alias || owner.name}`;
    return name;
  };

  const pairingOptions = [
    { value: '', label: 'None' },
    ...candidates.map((d) => ({ value: String(d.id), label: optionLabel(d) })),
  ];

  return (
    <div className="mt-3">
      <Button
        variant="link"
        className="p-0 text-decoration-none"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? '▾' : '▸'} Advanced
      </Button>

      {open && (
        <div className="mt-2">
          <Form.Label className="mb-1">{label}</Form.Label>
          <BaseSelect
            isSearchable
            selectProps={{
              'aria-label': label,
              options: pairingOptions,
              value: findSelectOption(pairingOptions, selected),
              isDisabled: busy,
              // `selected` and onChange stay string-based, as the save path expects.
              onChange: (option) => onChange(option?.value ?? ''),
            }}
          />

          <Form.Text className="text-muted">{candidates.length === 0 ? emptyHint : hint}</Form.Text>

          {error && <div className="text-danger mt-1">{error}</div>}
        </div>
      )}
    </div>
  );
}
